/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../../device";
import { PlaybackContentType } from "../enums";
import { DefaultAdIdentitySource } from "./DefaultAdIdentitySource";
import type { ITivoPlusAdIdentitySource } from "./ITivoPlusAdIdentitySource";
import type { TTivoPlusValues } from "./TivoPlusPlaceholders";

const APP_NAME: string = "TiVoPlus";

/**
 * Platform identifier reported to content partners.
 *
 * One value for every media pane — TMIS and IPTV are both Unity, and the
 * TiVo Plus Client Ad Parameter Specification has a dedicated Unity value
 * rather than reusing the native clients' `TMIS`/`IPTV`.
 */
const PLATFORM: string = "unity";

/** IAB OpenRTB device type. */
enum OpenRtbDeviceType {
  TV = "3-Connected_TV",
  Fire_TV = "7-Set_Top_Box",
  DEFAULT = "2-Personal_Computer"
}

/** Advertising device-identifier type, keyed by platform. */
enum AdDeviceType {
  SAMSUNG = "tifa",
  LG = "lgudid",
  /** Covers Amazon Fire TV and Vega OS alike. */
  VEGA = "afai",
  /** Any other desktop or TV web browser. */
  DEFAULT = "dpid"
}

/** OS name reported by `TvProperties.getPlatformName()` for a macOS browser. */
const MACOS_PLATFORM_NAME: string = "macOS";

/** OS name reported by `TvProperties.getPlatformName()` for a Windows browser. */
const WINDOWS_PLATFORM_NAME: string = "Windows";

/** Device brand, keyed by platform. */
enum DeviceBrand {
  SAMSUNG = "Samsung",
  LG = "LG",
  AMAZON = "Amazon",
  APPLE = "Apple",
  MICROSOFT = "Microsoft",
  DEFAULT = ""
}

/** TIVOPLUS_CONTENT_TYPE value for live linear playback. */
const CONTENT_TYPE_LINEAR: string = "Linear";

/** TIVOPLUS_CONTENT_TYPE value for every other (non-live) playback flow. */
const CONTENT_TYPE_VOD: string = "Vod";

/** Ad unit classification for production traffic. */
const AD_UNIT_TYPE_PRODUCTION: string = "video";

/** Ad unit classification for every pre-production environment. */
const AD_UNIT_TYPE_TEST: string = "test_ad_unit";

/** Environment name that counts as production. */
const PRODUCTION_ENVIRONMENT: string = "production";

/** Characters permitted in the application version parameter. */
const UNSAFE_VERSION_CHARACTERS: RegExp = /[^a-zA-Z0-9-]/g;

/**
 * Assembles the device, application and privacy values a TiVo+ template
 * needs.
 *
 * Every source is synchronous, so this performs no I/O and can be called on
 * the tuning path without delaying playback. It is also deterministic within
 * a session — no timestamps, no randomness — which matters because the same
 * template is resolved twice during a tune, once before the session is
 * created and once from the session's own URL. Identical output is what lets
 * the second resolution be recognised as unchanged instead of forcing a
 * needless reload.
 */
export class TivoPlusValueProvider {
  private static oInstance: TivoPlusValueProvider | null = null;

  private mAdIdentitySource: ITivoPlusAdIdentitySource;

  private constructor(adIdentitySource: ITivoPlusAdIdentitySource) {
    this.mAdIdentitySource = adIdentitySource;
  }

  public static getInstance(): TivoPlusValueProvider {
    if (TivoPlusValueProvider.oInstance === null) {
      TivoPlusValueProvider.oInstance = new TivoPlusValueProvider(
        new DefaultAdIdentitySource()
      );
    }

    return TivoPlusValueProvider.oInstance;
  }

  /**
   * Replaces the advertising and privacy source.
   *
   * The seam that lets the interim web defaults be swapped once the
   * specification settles, without touching resolution itself.
   */
  public setAdIdentitySource(source: ITivoPlusAdIdentitySource): void {
    this.mAdIdentitySource = source;
  }

  /** Drops the singleton. Used by tests. */
  public static reset(): void {
    TivoPlusValueProvider.oInstance = null;
  }

  /** Snapshot of every value a template can reference. */
  public getValues(playbackContentType: PlaybackContentType): TTivoPlusValues {
    const tvProperties = DeviceModel.getTVProperties();
    const tivoSettings = DeviceModel.getTiVoSettings();

    return {
      appName: APP_NAME,
      platform: PLATFORM,
      contentType: TivoPlusValueProvider.getContentType(playbackContentType),
      deviceBrand: TivoPlusValueProvider.getDeviceBrand(),
      openRtbDeviceType: TivoPlusValueProvider.getOpenRtbDeviceType(),
      adDeviceType: TivoPlusValueProvider.getAdDeviceType(),
      adUnitType: TivoPlusValueProvider.getAdUnitType(),
      limitAdTracking: this.mAdIdentitySource.isLimitAdTracking() ? "1" : "0",
      usPrivacy: this.mAdIdentitySource.getUsPrivacy(),
      gdpr: this.mAdIdentitySource.getGdpr(),
      gdprConsent: this.mAdIdentitySource.getGdprConsent(),
      tcString: TivoPlusValueProvider.getTcString(
        this.mAdIdentitySource.getGdpr(),
        this.mAdIdentitySource.getGdprConsent()
      ),
      // The following tokens will need to be replaced with a null string until
      // a consent management platform such as OneTrust is integrated.
      // Once OneTrust is integrated, it will generate the values to use for the tokens.
      // For the US/Canada I expect it to generate GPP values
      gppString: "",
      gppSidString: "",
      deviceLanguage: TivoPlusValueProvider.orEmpty(tvProperties.getLang()),
      deviceCountry: TivoPlusValueProvider.orEmpty(tvProperties.getRegion()),
      appVersion: TivoPlusValueProvider.getAppVersion(),
      adSize: TivoPlusValueProvider.getAdSize(),

      advertisingId: this.mAdIdentitySource.getAdvertisingId(),
      // Documented as needing to persist for at least 24 hours. The device
      // UUID satisfies that; note it is currently a fixed value on this
      // platform, so it does not yet distinguish devices.
      userSessionId: TivoPlusValueProvider.orEmpty(DeviceModel.getDeviceUuid()),
      mvpd: TivoPlusValueProvider.orEmpty(tivoSettings.getMsoName()),
      deviceMake: TivoPlusValueProvider.getDeviceBrand(),
      deviceModel: TivoPlusValueProvider.orEmpty(tvProperties.getDeviceModel())
    };
  }

  /**
   * TIVOPLUS_CONTENT_TYPE, keyed off the playback flow: LIVE is linear,
   * every other flow (NDVR, start-over, catch-up, VOD) is reported as VOD.
   */
  private static getContentType(
    playbackContentType: PlaybackContentType
  ): string {
    return playbackContentType === PlaybackContentType.LIVE
      ? CONTENT_TYPE_LINEAR
      : CONTENT_TYPE_VOD;
  }

  /**
   * TIVOPLUS_TC_STRING, the IAB TCF consent string. Mirrors `gdprConsent`
   * only when both `gdpr` and `gdprConsent` carry a value; empty otherwise,
   * since a consent string without a GDPR applicability flag (or vice versa)
   * is not meaningful to the ad server.
   */
  private static getTcString(gdpr: string, gdprConsent: string): string {
    return gdpr !== "" && gdprConsent !== "" ? gdprConsent : "";
  }

  /** IAB OpenRTB device type, keyed off the detected device category. */
  private static getOpenRtbDeviceType(): string {
    if (DeviceModel.isVegOsPlatform()) {
      return OpenRtbDeviceType.Fire_TV;
    }
    return DeviceModel.isTvDevice()
      ? OpenRtbDeviceType.TV
      : OpenRtbDeviceType.DEFAULT;
  }

  /** Advertising device-identifier type, keyed off the platform in use. */
  private static getAdDeviceType(): string {
    if (DeviceModel.isTizenPlatform()) {
      return AdDeviceType.SAMSUNG;
    }
    if (DeviceModel.isWebOsPlatform()) {
      return AdDeviceType.LG;
    }
    if (DeviceModel.isVegOsPlatform()) {
      return AdDeviceType.VEGA;
    }
    return AdDeviceType.DEFAULT;
  }

  /**
   * TIVOPLUS_DEVBRAND, keyed off the detected platform: TV platforms report
   * their manufacturer, and desktop browsers report the OS vendor.
   */
  private static getDeviceBrand(): string {
    if (DeviceModel.isTizenPlatform()) {
      return DeviceBrand.SAMSUNG;
    }
    if (DeviceModel.isWebOsPlatform()) {
      return DeviceBrand.LG;
    }
    if (DeviceModel.isVegOsPlatform()) {
      return DeviceBrand.AMAZON;
    }

    const platformName = DeviceModel.getTVProperties().getPlatformName();
    if (platformName === MACOS_PLATFORM_NAME) {
      return DeviceBrand.APPLE;
    }
    if (platformName === WINDOWS_PLATFORM_NAME) {
      return DeviceBrand.MICROSOFT;
    }

    return DeviceBrand.DEFAULT;
  }

  /**
   * Pre-production traffic is reported as test inventory so it is excluded
   * from live ad reporting.
   */
  private static getAdUnitType(): string {
    return DeviceModel.getAppEnvironment() === PRODUCTION_ENVIRONMENT
      ? AD_UNIT_TYPE_PRODUCTION
      : AD_UNIT_TYPE_TEST;
  }

  /**
   * The application version, reduced to URL-safe characters.
   *
   * Mirrors the reference client, which strips anything outside
   * `[a-zA-Z0-9-]` so a build string carrying separators cannot break the
   * parameter it sits in.
   */
  private static getAppVersion(): string {
    const version = TivoPlusValueProvider.orEmpty(
      DeviceModel.getAppVersionName()
    );

    return version.replaceAll(UNSAFE_VERSION_CHARACTERS, "");
  }

  /** Screen size as `WIDTHxHEIGHT`, or empty when unavailable. */
  private static getAdSize(): string {
    const resolution = DeviceModel.getScreenResolution();

    if (resolution.width === 0 || resolution.height === 0) {
      return "";
    }

    return String(resolution.width) + "x" + String(resolution.height);
  }

  /** Normalises a possibly-absent device fact to a string. */
  private static orEmpty(value: string | undefined | null): string {
    return value === undefined || value === null ? "" : value;
  }
}
